'use client';

import * as React from 'react';
import { CheckIcon } from '@radix-ui/react-icons';
import { Loader2 } from 'lucide-react';

import { cn } from '../lib/utils';
import type { OptionItemProps } from './autocomplete-types';
import type { AutocompleteViewProps } from './use-autocomplete';

/**
 * A single option row + the full option list body for `Autocomplete`. Extracted
 * from `autocomplete.tsx` (conformance, code-size guard). Shared by the desktop
 * dropdown and the mobile sheet so their list rendering stays identical.
 */
export function OptionItem({
  option,
  isSelected,
  isHighlighted,
  onClick,
  renderOption,
  optionId,
}: OptionItemProps) {
  if (renderOption) {
    return (
      <button
        type="button"
        id={optionId}
        role="option"
        aria-selected={isSelected}
        aria-disabled={option.disabled}
        className={cn(
          'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left transition-colors',
          'min-h-[44px]', // Mobile-friendly touch target
          // Active (keyboard/hover) = neutral elevated surface, NOT the brand
          // accent (which is a saturated maroon in dark mode). Selected display
          // is owned by renderOption (e.g. a trailing check).
          isHighlighted && 'bg-foreground/[0.06]',
          option.disabled && 'cursor-not-allowed opacity-50',
        )}
        onClick={onClick}
        disabled={option.disabled}
      >
        {renderOption(option, isSelected)}
      </button>
    );
  }

  return (
    <button
      type="button"
      id={optionId}
      role="option"
      aria-selected={isSelected}
      aria-disabled={option.disabled}
      className={cn(
        'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left transition-colors',
        'min-h-[44px]', // Mobile-friendly touch target
        // Active (keyboard/hover) = neutral elevated surface, NOT the brand
        // accent (saturated maroon on dark). Selection is shown by the trailing
        // check + a faint tint, so the open state never slams a solid accent
        // block onto the pre-selected row.
        isHighlighted && 'bg-foreground/[0.06]',
        isSelected && !isHighlighted && 'bg-primary/[0.06]',
        option.disabled && 'cursor-not-allowed opacity-50',
      )}
      onClick={onClick}
      disabled={option.disabled}
    >
      {option.icon && <span className="flex-shrink-0">{option.icon}</span>}
      <div className="min-w-0 flex-1">
        <div className="text-foreground truncate font-medium">
          {option.label}
        </div>
        {option.description && (
          <div className="text-muted-foreground truncate text-sm">
            {option.description}
          </div>
        )}
      </div>
      {isSelected && (
        <CheckIcon className="text-primary h-4 w-4 flex-shrink-0" />
      )}
    </button>
  );
}

/**
 * The list body rendered inside the dropdown (desktop) and the sheet (mobile):
 * loading state, empty state, grouped/flat options, and the optional
 * create-new-option button.
 */
export function AutocompleteOptionList({
  props,
  state,
}: AutocompleteViewProps) {
  const { loading, loadingMessage, emptyMessage, renderOption } = props;
  const {
    filteredOptions,
    canCreate,
    highlightedIndex,
    isCreating,
    inputValue,
    baseId,
    selectedValues,
    handleSelect,
    handleCreate,
  } = state;

  if (loading) {
    return (
      <div className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        {loadingMessage}
      </div>
    );
  }

  if (filteredOptions.length === 0 && !canCreate) {
    return (
      <div className="text-muted-foreground py-6 text-center text-sm">
        {emptyMessage}
      </div>
    );
  }

  // When any option carries a `group`, render bucketed under group headings
  // (first-seen order). The `index` passed to each OptionItem stays its
  // absolute position in `filteredOptions` so keyboard nav / highlight /
  // Enter selection keep working unchanged across grouped rendering.
  const hasGroups = filteredOptions.some((opt) => opt.group);

  return (
    <>
      {hasGroups
        ? (() => {
            let lastGroup: string | undefined;
            return filteredOptions.map((option, index) => {
              const showHeading = option.group && option.group !== lastGroup;
              lastGroup = option.group;
              return (
                <React.Fragment key={option.value}>
                  {showHeading && (
                    <div
                      role="presentation"
                      className="text-muted-foreground px-3 pt-3 pb-1 text-[11px] font-semibold tracking-wider uppercase"
                    >
                      {option.group}
                    </div>
                  )}
                  <OptionItem
                    option={option}
                    optionId={`${baseId}-option-${index}`}
                    isSelected={selectedValues.includes(option.value)}
                    isHighlighted={highlightedIndex === index}
                    onClick={() => handleSelect(option.value)}
                    renderOption={renderOption}
                  />
                </React.Fragment>
              );
            });
          })()
        : filteredOptions.map((option, index) => (
            <OptionItem
              key={option.value}
              option={option}
              optionId={`${baseId}-option-${index}`}
              isSelected={selectedValues.includes(option.value)}
              isHighlighted={highlightedIndex === index}
              onClick={() => handleSelect(option.value)}
              renderOption={renderOption}
            />
          ))}
      {canCreate && (
        <button
          type="button"
          id={`${baseId}-option-${filteredOptions.length}`}
          role="option"
          aria-selected={false}
          className={cn(
            'flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left transition-colors',
            'min-h-[44px]',
            highlightedIndex === filteredOptions.length &&
              'bg-foreground/[0.06]',
          )}
          onClick={handleCreate}
          disabled={isCreating}
        >
          {isCreating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <span className="text-primary">+</span>
          )}
          <span>Create &quot;{inputValue.trim()}&quot;</span>
        </button>
      )}
    </>
  );
}
